import React, { useState } from "react";
import styles from "./Home.module.css";
import SEO from "../../seo/SEO";
import { seoConfig } from "../../seo/seoConfig";
// Adjust these import paths to match your project structure
import LazyImage from "../../components/LazyImage/LazyImage";
import CtaBanner from "../../components/CtaBanner/CtaBanner";
import { ifillip_ai_dashboard as HERO_IMAGE } from "../../controller/Image";

/* ------------------------------------------------------------------
   STATIC CONTENT — kept outside the component for clean JSX.
   Icons use Font Awesome 6 (e.g. "fa-solid fa-brain").
------------------------------------------------------------------ */

const FEATURES = [
  {
    title: "Personalized Teaching",
    description:
      "AI-driven insights that help educators tailor instruction to every student's pace.",
    icon: "fa-solid fa-brain",
    variant: "primary",
  },
  {
    title: "Adaptive Learning",
    description:
      "Curriculum content that adjusts in real-time based on each student's performance.",
    icon: "fa-solid fa-book-open-reader",
    variant: "purple",
  },
  {
    title: "Stakeholder Communications",
    description:
      "Unified channels that connect parents, teachers, and administrators effortlessly.",
    icon: "fa-solid fa-comments",
    variant: "cyan",
  },
  {
    title: "Automated Attendance",
    description:
      "Eliminate manual tracking with RFID and AI-powered facial recognition systems.",
    icon: "fa-solid fa-fingerprint",
    variant: "warning",
  },
  {
    title: "Online Admissions",
    description:
      "Streamline enrollment from application to verification - fully digital, end to end.",
    icon: "fa-solid fa-clipboard-check",
    variant: "success",
  },
];

const STAKEHOLDERS = [
  {
    title: "Teachers",
    points: ["Smart recommendations", "Student tracking", "Reduced workload"],
    icon: "fa-solid fa-chalkboard-user",
  },
  {
    title: "Students",
    points: ["Adaptive learning", "Personalized growth"],
    icon: "fa-solid fa-graduation-cap",
  },
  {
    title: "Parents",
    points: ["Transparent communication", "Real-time updates"],
    icon: "fa-solid fa-people-roof",
  },
  {
    title: "Management",
    points: ["Data-driven insights", "Simplified operations"],
    icon: "fa-solid fa-chart-line",
  },
];

const FAQS = [
  {
    question: "What is a school management system?",
    answer:
      "A school management system is a digital platform that helps schools manage academic, administrative, and communication activities from one centralized system. Modern AI-powered school management systems like iFillip streamline attendance, admissions, assessments, lesson planning, parent communication, and student performance tracking while improving operational efficiency. With AI-driven personalized learning and analytics, schools can enhance teaching quality, student engagement, and data-driven decision-making.",
  },
  {
    question: "How to install a smart school management system?",
    answer:
      "To install a smart school management system like iFillip, start by identifying your school’s academic and administrative requirements. iFillip provides a cloud-based platform that allows schools to configure user access for administrators, teachers, students, and parents while securely migrating existing data such as admissions, attendance, and academic records. Once the setup is complete, iFillip enables AI-powered features such as adaptive learning, intelligent analytics, attendance tracking, communication management, and learning management to streamline and modernize school operations.",
  },
  {
    question: "Which software is best for school management?",
    answer:
      "The best school management software depends on a school’s size, academic requirements, automation needs, and budget. Platforms like iFillip provide AI-powered features for academic management, admissions, attendance tracking, analytics, adaptive learning, and communication within a single cloud-based system. With bilingual support (Tamil & English), mobile accessibility, and scalable modules, iFillip helps schools improve administrative efficiency and enhance the overall learning experience.",
  },
  {
    question: "What is an AI school?",
    answer:
      "An AI school uses Artificial Intelligence (AI) technologies to improve teaching, learning, and overall school management. AI-powered school management systems like iFillip help automate processes such as attendance, admissions, timetable management, assessments, and communication while providing personalized learning experiences and real-time student performance insights. By integrating analytics, adaptive learning, and smart automation, AI schools can enhance academic outcomes, improve administrative efficiency, and create a more connected learning environment for students, teachers, and parents.",
  },
  {
    question: "What is Smart School used for?",
    answer:
      "Smart School helps schools streamline academic and administrative operations through AI-powered education management. Platforms like iFillip enable schools to manage admissions, attendance, assessments, communication, lesson planning, and student performance from a single digital platform while supporting personalized learning, adaptive assessments, and real-time analytics for better academic outcomes.",
  },
  {
    question: "What is the best app for school?",
    answer:
      "The best school app combines academic management, personalized learning, communication, and automation in one platform. iFillip is an AI-powered school management system that offers adaptive learning, AI analytics, online attendance, and digital admissions to improve teaching quality, student engagement, administrative efficiency, and parent communication.",
  },
  {
    question: "What is AI-based learning?",
    answer:
      "AI-based learning uses Artificial Intelligence to personalize education based on each student's learning pace, performance, and behavior. For example, iFillip uses AI-driven analytics, adaptive assessments, and smart recommendations to improve student engagement, academic outcomes, and classroom efficiency while simplifying school management.",
  },
  {
    question: "What is the cost of school management software in India?",
    answer:
      "The cost of school management software in India varies based on features, number of users, deployment type, and hosting requirements. For example, products like iFillip offer flexible License and SaaS (cloud-based) pricing models tailored to each institution’s needs, with scalable support for web and mobile access, RFID integration, bilingual functionality, analytics, and AI-powered features.",
  },
];
/* ------------------------------------------------------------------
   COMPONENT
------------------------------------------------------------------ */

const Home = () => {
  const [openFaq, setOpenFaq] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? -1 : index);
  };

  return (
    <>
      <SEO {...seoConfig.home} />
      {/* ... rest of your Home page content ... */}
      <main className={styles.home}>
        {/* ============== TOP BANNER (NEW) ============== */}
        <section className={styles.topBanner}>
          <span
            className={`${styles.topBannerGlow} ${styles.topBannerGlowLeft}`}
            aria-hidden="true"
          />
          <span
            className={`${styles.topBannerGlow} ${styles.topBannerGlowRight}`}
            aria-hidden="true"
          />
          <div className="container">
            <h1 className={styles.topBannerTitle}>
              <i
                className={`fa-solid fa-wand-magic-sparkles ${styles.topBannerIcon}`}
                aria-hidden="true"
              />
              <span>Empowering Educators and Enabling Adaptive Learning </span>
            </h1>
          </div>
        </section>

        {/* ============== HERO ============== */}
        <section className={styles.hero}>
          <div className="container">
            <div className="row align-items-start g-5">
              <div className="col-lg-6 px-0">
                <div className={styles.schemaContainer}>
                  <span className={styles.badge}>iFillip</span>

                  <h1 className={styles.heroTitle}>
                    Smart, Affordable, and Future-Ready Technology Transforming
                    Education
                  </h1>

                  <p
                    className={`mt-2 ${styles.normalText} ${styles.aiTagline}`}
                  >
                    An AI-powered education and school management system
                  </p>
                </div>

                <div className="mt-2">
                  <a href="/book-demo" className={styles.ctaButton}>
                    Book a Demo
                  </a>
                </div>
              </div>

              <div className="col-lg-6 px-0">
                <div className={styles.heroImageWrap}>
                  <div className={styles.glassFrame}>
                    <LazyImage
                      {...HERO_IMAGE}
                      alt="iFillip dashboard interface preview"
                      wrapperClassName={styles.heroImage}
                    />
                  </div>
                  <span className={`${styles.blob} ${styles.blobTop}`} />
                  <span className={`${styles.blob} ${styles.blobBottom}`} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============== FEATURES ============== */}
        <section className={`${styles.section} ${styles.bgSurface}`}>
          <div className="container px-0">
            <div className="text-center mx-auto mb-5" style={{ maxWidth: 720 }}>
              <h2 className={styles.sectionTitle}>
                Redefining Every Aspect of Education
              </h2>
            </div>

            <div className="row g-4 justify-content-center">
              {FEATURES.map((feature, index) => (
                <div
                  className={`${index < 3 ? "col-lg-4" : "col-lg-6"} col-md-6 ps-0`}
                  key={feature.title}
                >
                  <article className={styles.featureCard}>
                    <div
                      className={`${styles.featureIcon} ${
                        styles[`icon${capitalize(feature.variant)}`]
                      }`}
                    >
                      <i className={feature.icon} aria-hidden="true" />
                    </div>
                    <h3 className="subTitle">{feature.title}</h3>
                    <p className="normalText">{feature.description}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============== VALUE ADDS / STAKEHOLDERS ============== */}
        <section className={`${styles.section} ${styles.bgAlt}`}>
          <div className="container">
            <h2 className={`${styles.sectionTitle} text-center mb-5`}>
              Value Additions to Stakeholders
            </h2>

            <div className="row g-4">
              {STAKEHOLDERS.map((person) => (
                <div className="col-sm-6 col-lg-3 ps-0" key={person.title}>
                  <div className={styles.stakeholderCard}>
                    <div className={styles.stakeholderIconWrap}>
                      <i className={person.icon} aria-hidden="true" />
                    </div>
                    <h3 className="subTitle text-center">{person.title}</h3>
                    <ul className="WhyIfillip_insightsList__jLRSO">
                      {person.points.map((point) => (
                        <li key={point} className="normalText text-start">
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* ============== FAQ ============== */}
        <section className={`${styles.section} ${styles.bgSurface}`}>
          <div className="container">
            <h2 className={`${styles.sectionTitle} text-center mb-5`}>
              Frequently Asked Questions
            </h2>

            <div className="row g-4">
              {FAQS.map((faq, index) => (
                <div className="col-lg-12 px-0" key={faq.question}>
                  <div
                    className={`${styles.faqCard} ${
                      openFaq === index ? styles.faqCardOpen : ""
                    }`}
                  >
                    <button
                      className={styles.faqButton}
                      onClick={() => toggleFaq(index)}
                      aria-expanded={openFaq === index}
                      aria-controls={`faq-answer-${index}`}
                    >
                      <span className="subTitle">{faq.question}</span>
                      <i
                        className={`${
                          openFaq === index
                            ? "fa-solid fa-chevron-up"
                            : "fa-solid fa-chevron-down"
                        } ${styles.faqIcon}`}
                        aria-hidden="true"
                      />
                    </button>
                    {openFaq === index && (
                      <div
                        id={`faq-answer-${index}`}
                        className="mt-2 normalText"
                      >
                        {faq.answer}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============== FINAL CTA BANNER ============== */}
        <section className={`${styles.ctaWrap} page-container`}>
          <CtaBanner
            title="Choose iFillip and Transform Your School into a Smarter, Connected Campus"
            // subtitle="Transform the way your school operates with iFillip's intelligent management platform. From administration, attendance tracking, fee management, and parent communication to academic monitoring, performance analytics, and reporting, manage every aspect of your institution efficiently through one unified system while improving transparency and academic outcomes. Book your free demo consultation now."
            primaryLabel="Book a Demo"
            primaryHref="/book-demo"
          />
        </section>
      </main>
    </>
  );
};

/* Helper: capitalize first letter (for dynamic class names) */
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export default Home;
